import { UserLocation } from './locations';

// Primary: GeoJS (CORS-enabled, no key)
const PRIMARY_API = 'https://get.geojs.io/v1/ip/geo.json';

// Secondary: ipwho.is (CORS-enabled, no key)
const SECONDARY_API = 'https://ipwho.is/';

// Tertiary: ipapi.co (may block CORS in some regions; keep as last resort)
const TERTIARY_API = 'https://ipapi.co/json/';

/**
 * Detect user's location based on their IP address
 * Uses free geolocation APIs with fallback
 */
export const detectUserLocation = async (): Promise<UserLocation | null> => {
  try {
    // 1) GeoJS
    try {
      const res = await fetch(PRIMARY_API, { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        return {
          country: data.country,
          countryCode: data.country_code,
          state: data.region,
          city: data.city,
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude)
        };
      }
    } catch {}

    // 2) ipwho.is
    try {
      const res2 = await fetch(SECONDARY_API, { headers: { 'Accept': 'application/json' } });
      if (res2.ok) {
        const d = await res2.json();
        if (d.success !== false) {
          return {
            country: d.country,
            countryCode: d.country_code,
            state: d.region,
            city: d.city,
            latitude: Number(d.latitude),
            longitude: Number(d.longitude)
          };
        }
      }
    } catch {}

    // 3) ipapi.co (may CORS-fail; ignore errors)
    try {
      const res3 = await fetch(TERTIARY_API, { headers: { 'Accept': 'application/json' } });
      if (res3.ok) {
        const data3 = await res3.json();
        return {
          country: data3.country_name,
          countryCode: data3.country_code,
          state: data3.region,
          city: data3.city,
          latitude: parseFloat(data3.latitude),
          longitude: parseFloat(data3.longitude)
        };
      }
    } catch {}

  } catch (error) {
    console.error('Error detecting user location:', error);
    return null;
  }
  return null;
};

/**
 * Get location from browser's Geolocation API (requires user permission)
 * This is more accurate than IP-based location
 */
export const getBrowserLocation = (): Promise<GeolocationPosition | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser');
      resolve(null);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => {
        console.warn('Browser geolocation error:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  });
};

/**
 * Reverse geocode coordinates to get city/state/country
 * Uses Nominatim (OpenStreetMap) - free, no API key required
 */
export const reverseGeocode = async (
  latitude: number,
  longitude: number
): Promise<UserLocation | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'BelivahApp/1.0' // Required by Nominatim
        }
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      const address = data.address;
      
      return {
        country: address.country,
        countryCode: address.country_code?.toUpperCase() || '',
        state: address.state || address.province || address.region,
        city: address.city || address.town || address.village,
        latitude,
        longitude
      };
    }
    
    return null;
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    return null;
  }
};

/**
 * Combined location detection strategy
 * 1. Try IP-based detection (fast, no permission needed)
 * 2. Optionally try browser geolocation if user permits (more accurate)
 */
export const getUserLocation = async (useBrowserLocation = false): Promise<UserLocation | null> => {
  // First, try IP-based detection
  let location = await detectUserLocation();
  
  // If user opted for browser location and IP detection succeeded
  if (useBrowserLocation && location) {
    const browserPos = await getBrowserLocation();
    
    if (browserPos) {
      // Use reverse geocoding to get more accurate city/state
      const preciseLocation = await reverseGeocode(
        browserPos.coords.latitude,
        browserPos.coords.longitude
      );
      
      if (preciseLocation) {
        location = preciseLocation;
      }
    }
  }
  
  return location;
};

/**
 * Format location for display
 */
export const formatLocation = (location: UserLocation | null): string => {
  if (!location) return 'Unknown';
  
  const parts = [];
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.country) parts.push(location.country);
  
  return parts.join(', ') || 'Unknown';
};

/**
 * Check if location data is stale (older than 7 days)
 */
export const isLocationStale = (timestamp: number): boolean => {
  const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp > ONE_WEEK;
};

/**
 * Store location in localStorage with timestamp
 */
export const cacheLocation = (location: UserLocation): void => {
  localStorage.setItem('userLocation', JSON.stringify({
    ...location,
    timestamp: Date.now()
  }));
};

/**
 * Get cached location if not stale
 */
export const getCachedLocation = (): UserLocation | null => {
  try {
    const cached = localStorage.getItem('userLocation');
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    
    // Check if stale
    if (isLocationStale(data.timestamp)) {
      localStorage.removeItem('userLocation');
      return null;
    }
    
    return {
      country: data.country,
      countryCode: data.countryCode,
      state: data.state,
      city: data.city,
      latitude: data.latitude,
      longitude: data.longitude
    };
  } catch (error) {
    return null;
  }
};
